import React, { useEffect, ComponentType, ReactNode, useContext } from 'react';
import { userAnalytics, civicAnalytics } from '.';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import Log from '../../common/logging';
import { CivicAnalyticsEvent } from './civic/types';

const shouldSendAnalytics = (civicAnalyticsEvent: CivicAnalyticsEvent | undefined) =>
  process.env.REACT_APP_SEND_CIVIC_ANALYTICS === 'true' ||
  (!civicAnalyticsEvent?.redirectUrl?.includes('localhost') &&
    !civicAnalyticsEvent?.referrer?.includes('localhost') &&
    !civicAnalyticsEvent?.domain?.includes('localhost'));

// Used for flushing the analytics buffer when the iframe is closed.
const useBeforeUnload = (handler: () => Promise<void>) => {
  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      await handler();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ''; // Legacy method for cross-browser support
    };

    // We detect 'page unload' in one of several ways, with fallback as follows:
    // 1. visibilitychange to 'hidden'. Most reliable but not implemented by all browsers.
    // 2. pagehide: Unreliable on mobile but supported on a wider range of browsers
    // 3. beforeunload: Unreliable on mobile but supported on a wider range of browsers
    // This is described under "Avoid unload and beforeunload" here: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon

    if (document.visibilityState) {
      Log.debug('Browser supports visibilitychange event. Registering listener for it.');
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          Log.debug('visibilitychange to hidden. Flushing analytics events');
          handler();
        }
      });
      return;
    }

    if ('onpagehide' in window) {
      Log.debug('Browser supports pagehide event. Registering listener for it.');
      window.addEventListener('pagehide', () => {
        Log.debug('pagehide event fired. Flushing analytics events');
        handler();
      });
      return;
    }

    // If all else fails, use beforeunload
    Log.debug('Adding beforeunload listener');
    (window as Window).addEventListener('beforeunload', handleBeforeUnload);

    // eslint-disable-next-line consistent-return
    return () => {
      Log.debug('Removing beforeunload listener');
      (window as Window).removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handler]);
};

const withRouteAnalytics = <P extends object>(
  WrappedComponent: ComponentType<P>,
  routeName: string,
): ComponentType<P> => {
  const WithRouteAnalytics: React.FC<P> = (props) => {
    const { civicAnalyticsEvent } = useContext(FlowConfigContext);

    /** any time the analytics object changes, send to Civic, if not running locally */
    useEffect(() => {
      Log.debug('withRouteAnalytics civicAnalyticsEvent', { civicAnalyticsEvent, routeName });
      if (civicAnalyticsEvent && shouldSendAnalytics(civicAnalyticsEvent)) {
        civicAnalytics.enqueueAnalyticsEvent(civicAnalyticsEvent, routeName);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [civicAnalyticsEvent, routeName]);

    useBeforeUnload(async () => {
      await civicAnalytics.flushAnalyticsBuffer();
    });

    /** Only post to GA on page load */
    useEffect(() => {
      userAnalytics.pageView(routeName);
    }, []);

    return (<WrappedComponent {...props} />) as ReactNode;
  };

  return WithRouteAnalytics;
};

export default withRouteAnalytics;
